// Card 13, labelled isobaths (R548, ui-map-interface.md §17.3.13).
//
// The current chart draws depth contours and BREAKS each line for its
// figure. Three contours ring every trench OUTSIDE its rim, at 14, 32 and 54
// world px out, and each one carries a depth in metres in the gap. The
// rings say where the trench is from the open water a fleet approaches it
// over, which is what the owner asked for on 2026-09-05 (R557): the first
// build stepped the contours INSIDE the rim.
//
// **UNDER THE TERRAIN LOOK `chart`, A RIDGE IS RINGED TOO (R3276).** The
// look draws the rock flat, and its contour rings move out into the water:
// three rings inside the first water cell beside the rim (`ridge_offsets`).
// No other look draws a ridge ring, so the default picture does not change.
//
// **THE LINE IS TRACED, NOT A SHADER TERM.** The terrain composite already
// draws iso-lines of its own channel, and it could draw three more for
// about three ALU. It cannot break one for a figure: a shader has no arc
// length and no idea where the gap should be. And the plane carries no
// distance outside a body at all. So the rings are traced on the CPU
// (`outerIsobathsOf`): the rim is read off the SAME plane the composite
// reads, a distance raster is laid round it, and the piece walks each loop
// to place the break.
//
// **THE GAP SITS AT THE LOOP'S LOWEST-Y POINT**, which is the top of the
// picture. That is where the card put it, and it is the one point of a loop
// that a reader finds without looking: the crown of the contour.
//
// **A RING DOES NOT CROSS ROCK OR ANOTHER TRENCH.** A run is cut where a
// point stands on a blocked cell, so a ring passes behind a ridge and stops
// at another trench's rim: a chart's isobath is drawn on the water.
//
// **A RING ENDS AT THE MAP EDGE (R2211).** The ring raster reaches past the
// plane, so every ring comes back closed, also round a trench on the edge
// (R2368). The piece cuts each loop into its pieces on the map, and each
// open piece takes its own gap and figure.
//
// **THE FIGURES ARE A CHART'S DEPTH SERIES (R3276).** The three rings of a
// body take three values of `series_m` that agree with the sounding of the
// water round the body (`ringFigures`): deeper than it round a trench, at
// or below it round a ridge. With the band field (`Hello.depth`) that
// water is the median sounding of the open cells that touch the body, the
// answer of `soundingMetres` that card 02 writes, so the soundings, the
// depth tints and the isobaths tell one depth story (v2-the-deep.md §13).
// With no band field it is the top of the shelf band.

import { CHART, TERRAIN_STYLE_FIGURES, TERRAIN_TRENCH_RIM } from '../../../gen/presentation';
import { outerIsobathsOf } from '../../../render/terrain-features';
import type { Feature, TerrainPlane } from '../../../render/terrain-features';
import { thinLoop } from '../../../render/offset-loop';
import { polylineLength, type Pt } from '../../../render/text-run';
import { COLS, ROWS, cellAt, neighborAt } from '../../../world';
import { bandTopMetres, soundingMetres } from './soundings';
import type { Loop } from '../../../render/contours';
import type { ChartContext, ChartReading, ChartSinks, Piece } from '../context';
import { asPts, crownOf, faceOf, lengthTo } from './shared';

/** The figures the piece reads out of its own table. */
const T = CHART.pieces.isobaths;

/**
 * Half the length of the straight path a figure is laid along, centred on
 * the gap and turned to the contour's tangent. It only has to be longer
 * than half the widest figure.
 */
const LABEL_HALF = 64;

/** The terrain look whose ridges take rings (R3276). */
const RING_RIDGES_LOOK = 'chart';

/** What kind of body a ring stands round. */
export type RingKind = 'trench' | 'ridge';

/** One body the rings stand round, and the open cells that touch it. */
interface Body {
  kind: RingKind;
  /** The cells of the body: a trench's floor is its deepest sounding. */
  cells: readonly number[];
  /** The open cells that touch the body: "the water round" it. */
  shore: readonly number[];
}

/** One traced contour, broken for its figure. */
interface Contour {
  /** The loop, thinned, with the gap already taken out of it. */
  runs: Loop[];
  /** The two-point path the figure rides, through the crown of the gap. */
  label: Pt[];
  /** Which of the three offsets this is. It picks the dash and the alpha. */
  band: number;
  /** The index of the body in `IsobathsPiece.bodies`. */
  body: number;
}

/** The three dash patterns, flattened by the emitter into one key each. */
function dashOf(band: number): readonly number[] {
  const a = T.arr as Record<string, readonly number[]>;
  return a[`dash_${band}`] ?? [];
}

export class IsobathsPiece implements Piece {
  readonly id = 'isobaths' as const;
  readonly tier = 'sea' as const;
  readonly pack = 'static' as const;
  /** The sea the fight happened in. It stays under a replay film. */
  readonly film = 'keep' as const;
  /**
   * The trace is zoom-free, so a band crossing is not a reason: the piece
   * keeps its geometry across the gate and repacks from it (R548). A
   * reading changes the figure alone, and the trace is reused for it.
   */
  readonly rebuildOn = ['hello', 'reading', 'survey', 'look'] as const;

  private contours: Contour[] = [];
  private bodies: Body[] = [];
  /** The plane the cached trace was read from, and the trace itself. */
  private tracedPlane: TerrainPlane | null = null;
  private traced: Loop[][][] = [];
  /** The ridge rings of the same plane, traced on the first rebuild under
   *  the look `chart` and kept across a flip of the look (R3276). */
  private ridgeTraced: Loop[][][] | null = null;
  /** How many times a plane was traced. The test reads it. */
  traces = 0;
  /** The trace, the blocked mask and the look the contours were cut from. */
  private cutTrace = -1;
  private cutBlocked: Uint8Array | null = null;
  private cutRidges = false;
  /** How many times the traced loops were cut. The test reads it. */
  cuts = 0;
  /** The reading the labels were laid out for. */
  private laid: ChartReading = 'modern';

  /**
   * The plane arrives about two animation frames after the hello, so the
   * piece waits for it here. The controller builds a piece the first frame
   * its gate holds, which is the frame the plane lands.
   */
  visible(ctx: ChartContext): boolean {
    return ctx.terrain !== null && (ctx.scene.plane ?? null) !== null;
  }

  rebuild(ctx: ChartContext): void {
    this.laid = ctx.reading;
    const plane = ctx.scene.plane ?? null;
    const terrain = ctx.terrain;
    if (!plane || !terrain) { this.contours = []; this.cutTrace = -1; return; }
    const gap = T.fig.gap_px;
    if (this.tracedPlane !== plane) {
      // One trace per plane. It is the one expensive step of the piece, and
      // a reading change or a repack must not pay it again.
      this.traced = terrain.trenches.map((feature) => outerIsobathsOf(
        plane, feature, T.arr.offsets, COLS, ROWS, T.fig.rim_px, T.fig.field_texels,
      ));
      this.ridgeTraced = null;
      this.tracedPlane = plane;
      this.traces++;
    }
    const ridges = ctx.scene.terrainLook === RING_RIDGES_LOOK;
    if (ridges && this.ridgeTraced === null) {
      this.ridgeTraced = terrain.ridges.map((feature) => outerIsobathsOf(
        plane, feature, T.arr.ridge_offsets, COLS, ROWS, T.fig.rim_px, T.fig.field_texels,
      ));
      this.traces++;
    }
    const blocked = ctx.scene.blocked;
    // The cut reads the trace, the blocked mask and the look alone, so a
    // survey and a reading keep it (R2897): the figures read the bands at
    // the pack.
    if (this.cutTrace === this.traces && this.cutBlocked === blocked
      && this.cutRidges === ridges) return;
    this.cutTrace = this.traces;
    this.cutBlocked = blocked;
    this.cutRidges = ridges;
    this.cuts++;
    this.contours = [];
    this.bodies = [];
    const cutBody = (feature: Feature, kind: RingKind, bands: Loop[][]): void => {
      const body = this.bodies.length;
      this.bodies.push({ kind, cells: feature.cells, shore: shoreOf(feature, blocked) });
      const turns: readonly number[] = kind === 'ridge' ? T.arr.ridge_turn_deg : [];
      bands.forEach((loops, band) => {
        const turn = ((turns[band] ?? 0) * Math.PI) / 180;
        for (const raw of loops) {
          const loop = thinLoop(raw, T.fig.step_px);
          // A ring that leaves the map is cut into its pieces on the map,
          // and each piece is cut on its own.
          const pieces = onMap(loop);
          const cuts = pieces === null
            ? [cutLoop(loop, gap, turn)]
            : pieces.map((run) => cutOpen(run, gap, turn));
          for (const cut of cuts) {
            if (!cut) continue;
            cut.band = band;
            cut.body = body;
            if (blocked) cut.runs = cut.runs.flatMap((run) => onWater(run, blocked));
            this.contours.push(cut);
          }
        }
      });
    };
    this.traced.forEach((bands, i) => cutBody(terrain.trenches[i], 'trench', bands));
    if (ridges && this.ridgeTraced) {
      this.ridgeTraced.forEach((bands, i) => cutBody(terrain.ridges[i], 'ridge', bands));
    }
  }

  emit(sinks: ChartSinks, ctx: ChartContext): void {
    const face = faceOf(ctx.reading);
    const depth = ctx.state.depthBands;
    const metres = depth ? soundingMetres(depth) : null;
    // The three figures of each body, read at the pack, because a survey
    // pass moves the soundings and keeps the cut (R2897).
    const figures = this.bodies.map((b) => bodyFigures(b, metres));
    for (const c of this.contours) {
      const alpha = T.arr.alphas[c.band] ?? 0;
      if (alpha <= 0) continue;
      const body = this.bodies[c.body];
      // A trench ring takes the trench rim ink of today; a ridge ring takes
      // the rim ink of the look that draws it (R3276).
      const ink = body.kind === 'ridge'
        ? TERRAIN_STYLE_FIGURES.chart.ridge.rim : TERRAIN_TRENCH_RIM.color;
      const dash = dashOf(c.band);
      const on = dash[0] ?? 0, off = dash[1] ?? 0;
      for (const run of c.runs) {
        for (let i = 1; i < run.length; i++) {
          sinks.segments.line(
            run[i - 1][0], run[i - 1][1], run[i][0], run[i][1],
            T.fig.width, ink, alpha, 0, on, off,
          );
        }
      }
      // A depth in metres in both readings: the face tells them apart.
      const figure = String(figures[c.body][c.band] ?? '');
      // The figure in the gap, turned to the contour's own tangent. The
      // path already reads left to right, so `upright` is off.
      ctx.glyphs.wordAlong(
        sinks.quads, figure, c.label, face, T.text.main, ink,
        Math.min(1, alpha + T.fig.figure_alpha_lift),
        { anchor: 'centre', startS: LABEL_HALF, upright: false },
      );
    }
  }

  /** How many contours the last rebuild traced. The test reads it. */
  get count(): number { return this.contours.length; }

  /** How many of them ring a ridge. The test reads it. */
  get ridgeCount(): number {
    return this.contours.filter((c) => this.bodies[c.body].kind === 'ridge').length;
  }

  /** The figures each body of the last cut takes over a band field, as
   *  `emit` writes them. The test reads it. */
  figuresOver(depth: Uint8Array | null): { kind: RingKind; figures: number[] }[] {
    const metres = depth ? soundingMetres(depth) : null;
    return this.bodies.map((b) => ({ kind: b.kind, figures: bodyFigures(b, metres) }));
  }

  /** The reading the labels stand in. The test reads it. */
  get reading(): ChartReading { return this.laid; }
}

/**
 * Breaks one closed loop for its figure and answers the one piece of line.
 *
 * The break is centred on the loop's lowest-y point. A loop shorter than
 * the gap itself is dropped: there would be no line left to draw, and a
 * figure floating on nothing states a depth for no contour.
 */
function cutLoop(loop: Loop, gap: number, turn = 0): Contour | null {
  if (loop.length < 3) return null;
  const total = polylineLength(asPts(loop), true);
  if (total <= gap * 2) return null;
  const crown = turn === 0 ? crownOf(loop) : crownToward(loop, turn);
  // The arc length of the crown, and the two ends of the break.
  const at = lengthTo(loop, crown);
  const half = gap / 2;
  const a = alongLoop(loop, at - half, total);
  const b = alongLoop(loop, at + half, total);
  // The run walks the loop from the far end of the break round to the
  // near end, so the whole contour is ONE polyline with the gap open.
  const run: Loop = [[b.x, b.y]];
  let s = 0;
  for (let i = 0; i < loop.length; i++) {
    const j = (crown + 1 + i) % loop.length;
    const prev = (j + loop.length - 1) % loop.length;
    s += Math.hypot(loop[j][0] - loop[prev][0], loop[j][1] - loop[prev][1]);
    if (s >= total - gap) break;
    run.push([loop[j][0], loop[j][1]]);
  }
  run.push([a.x, a.y]);
  return {
    runs: run.length >= 2 ? [run] : [],
    label: labelPath(a, b, loop[crown]),
    band: 0,
    body: 0,
  };
}

/**
 * The height of a point along the crown direction turned `turn` radians
 * toward the right: the y itself when the turn is 0. The smallest value is
 * the crown.
 */
function crownHeight(p: readonly [number, number], turn: number): number {
  return turn === 0 ? p[1] : p[1] * Math.cos(turn) - p[0] * Math.sin(turn);
}

/** The crown of a loop turned `turn` radians from the top (R3276). Of two
 *  points at one height the first wins, as `crownOf` rules. */
function crownToward(loop: Loop, turn: number): number {
  let crown = 0;
  for (let i = 1; i < loop.length; i++) {
    if (crownHeight(loop[i], turn) < crownHeight(loop[crown], turn)) crown = i;
  }
  return crown;
}

/** The point `s` px along a closed loop. */
function alongLoop(loop: Loop, s: number, total: number): { x: number; y: number } {
  let at = ((s % total) + total) % total;
  for (let i = 0; i < loop.length; i++) {
    const p = loop[i], q = loop[(i + 1) % loop.length];
    const len = Math.hypot(q[0] - p[0], q[1] - p[1]);
    if (len === 0) continue;
    if (at > len) { at -= len; continue; }
    const t = at / len;
    return { x: p[0] + (q[0] - p[0]) * t, y: p[1] + (q[1] - p[1]) * t };
  }
  return { x: loop[0][0], y: loop[0][1] };
}

/**
 * Breaks one open run for its figure, as `cutLoop` breaks a closed loop.
 *
 * The break is centred on the run's lowest-y point that has half the gap of
 * line on both sides, so the gap and the figure stand on the run. A run no
 * longer than two gaps is dropped, for the reason `cutLoop` states.
 */
function cutOpen(run: Loop, gap: number, turn = 0): Contour | null {
  if (run.length < 2) return null;
  const s = [0];
  for (let i = 1; i < run.length; i++) {
    s.push(s[i - 1] + Math.hypot(run[i][0] - run[i - 1][0], run[i][1] - run[i - 1][1]));
  }
  const total = s[s.length - 1];
  if (total <= gap * 2) return null;
  const half = gap / 2;
  let crown = -1;
  for (let i = 0; i < run.length; i++) {
    if (s[i] < half || s[i] > total - half) continue;
    if (crown < 0 || crownHeight(run[i], turn) < crownHeight(run[crown], turn)) crown = i;
  }
  if (crown < 0) return null;
  const a = alongOpen(run, s, s[crown] - half);
  const b = alongOpen(run, s, s[crown] + half);
  const head: Loop = run.filter((_, i) => s[i] < s[crown] - half);
  head.push([a.x, a.y]);
  const tail: Loop = [[b.x, b.y]];
  for (let i = 0; i < run.length; i++) if (s[i] > s[crown] + half) tail.push(run[i]);
  return {
    runs: [head, tail].filter((r) => r.length >= 2),
    label: labelPath(a, b, run[crown]),
    band: 0,
    body: 0,
  };
}

/**
 * The two-point path a figure rides: through the crown `c`, along the chord
 * of the gap from `a` to `b`, turned so the figure reads left to right.
 */
function labelPath(a: Pt, b: Pt, c: readonly [number, number]): Pt[] {
  const rot = upright(Math.atan2(b.y - a.y, b.x - a.x));
  const ux = Math.cos(rot), uy = Math.sin(rot);
  return [
    { x: c[0] - ux * LABEL_HALF, y: c[1] - uy * LABEL_HALF },
    { x: c[0] + ux * LABEL_HALF, y: c[1] + uy * LABEL_HALF },
  ];
}

/** The point at arc length `at` of an open run, whose arc lengths are `s`. */
function alongOpen(run: Loop, s: readonly number[], at: number): { x: number; y: number } {
  for (let i = 1; i < run.length; i++) {
    if (at > s[i]) continue;
    const len = s[i] - s[i - 1];
    const t = len === 0 ? 0 : (at - s[i - 1]) / len;
    const p = run[i - 1], q = run[i];
    return { x: p[0] + (q[0] - p[0]) * t, y: p[1] + (q[1] - p[1]) * t };
  }
  const last = run[run.length - 1];
  return { x: last[0], y: last[1] };
}

/**
 * The pieces of one traced loop that stand on the map, or null when the
 * whole loop does.
 *
 * The walk starts after a point off the map, so a piece that runs through
 * the loop's first point stays whole. Every traced ring is closed (R2368),
 * so the walk joins two pieces of line only where the loop itself runs.
 */
function onMap(loop: Loop): Loop[] | null {
  const off = loop.findIndex((p) => cellAt(p[0], p[1], COLS, ROWS) < 0);
  if (off < 0) return null;
  const out: Loop[] = [];
  let piece: Loop = [];
  for (let k = 1; k <= loop.length; k++) {
    const p = loop[(off + k) % loop.length];
    if (cellAt(p[0], p[1], COLS, ROWS) >= 0) { piece.push(p); continue; }
    if (piece.length >= 2) out.push(piece);
    piece = [];
  }
  return out;
}

/**
 * The chord's angle turned so the figure reads left to right.
 *
 * A ring outside the rim winds the other way round from a contour inside
 * it, because the deep side of the raster is now the far side, so the
 * chord across the gap runs right to left and the figure stood on its
 * head (owner, 2026-09-06). A chord that points left is turned by half a
 * turn; a vertical one keeps the way it came.
 */
function upright(rot: number): number {
  return Math.cos(rot) < -1e-9 ? rot + Math.PI : rot;
}

/**
 * One run cut into the pieces that stand on open water.
 *
 * A point on a blocked cell — rock, or another trench — ends the piece
 * before it, and the next open point starts a new one. A piece of one point
 * is dropped: there is no line in it.
 */
function onWater(run: Loop, blocked: Uint8Array): Loop[] {
  const out: Loop[] = [];
  let piece: Loop = [];
  for (const p of run) {
    const cell = cellAt(p[0], p[1], COLS, ROWS);
    const open = cell < 0 || blocked[cell] === 0;
    if (open) { piece.push(p); continue; }
    if (piece.length >= 2) out.push(piece);
    piece = [];
  }
  if (piece.length >= 2) out.push(piece);
  return out;
}

/**
 * The open cells that touch a body, in ascending index: "the water round"
 * it, whose median sounding sets the body's figures.
 */
function shoreOf(feature: Feature, blocked: Uint8Array | null): number[] {
  const seen = new Set<number>();
  for (const cell of feature.rimCells) {
    for (let k = 0; k < 6; k++) {
      const n = neighborAt(cell, k);
      if (n >= 0 && (!blocked || blocked[n] === 0)) seen.add(n);
    }
  }
  return [...seen].sort((a, b) => a - b);
}

/** The lower median of the soundings of some cells, or 0 when none of
 *  them carries a band. */
function medianSounding(cells: readonly number[], metres: Uint16Array): number {
  const got: number[] = [];
  for (const c of cells) if (metres[c] > 0) got.push(metres[c]);
  if (got.length === 0) return 0;
  got.sort((a, b) => a - b);
  return got[(got.length - 1) >> 1];
}

/** The three figures of one body, nearest ring first. */
function bodyFigures(body: Body, metres: Uint16Array | null): number[] {
  const sounded = metres ? medianSounding(body.shore, metres) : 0;
  // No band field, or no sounding on the shore: the water is the top of
  // the shelf band (R3276, the comment of `series_m`).
  const around = sounded > 0 ? sounded : bandTopMetres(1);
  let floor = Infinity;
  if (metres && body.kind === 'trench') {
    let deepest = 0;
    for (const c of body.cells) if (metres[c] > deepest) deepest = metres[c];
    if (deepest > 0) floor = deepest;
  }
  return ringFigures(body.kind, around, floor, T.arr.series_m);
}

/**
 * The figures of the three rings round one body, the ring nearest the body
 * first (R3276, `ui-map-interface.md` §17.3.13).
 *
 * A ridge takes the three values of the series at or below `around`, the
 * shallowest nearest the rock. A trench takes the three values deeper than
 * `around`, the deepest nearest the trench; a window that passes `floor`
 * (or the end of the series) moves back so that it ends at the deepest
 * value not past the floor. A series of fewer than three values gives
 * fewer figures.
 */
export function ringFigures(
  kind: RingKind, around: number, floor: number, series: readonly number[],
): number[] {
  const n = series.length;
  if (n === 0) return [];
  const least = Math.min(2, n - 1);
  if (kind === 'ridge') {
    let top = -1;
    for (let i = 0; i < n; i++) if (series[i] <= around) top = i;
    const end = Math.max(top, least);
    return series.slice(end - least, end + 1);
  }
  let start = n;
  for (let i = n - 1; i >= 0; i--) if (series[i] > around) start = i;
  let cap = -1;
  for (let i = 0; i < n; i++) if (series[i] <= floor) cap = i;
  const end = Math.max(Math.min(start + 2, cap, n - 1), least);
  return series.slice(end - least, end + 1).reverse();
}

/** The one instance the registry holds. */
export const isobaths = new IsobathsPiece();
